import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { ProductChangeRequestQueryApi } from "src/api/generated/erp/productChanges/api/productChangeRequestQueryApi.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { ProductChangeRequestView } from "src/api/generated/erp/db/types/tables/productChangeRequestView.ts";
import { enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getProductChangeRequestStateLabels } from "src/api/generated/erp/db/types/enums/productChangeRequestState.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { ProductChangeConnectionsApi } from "src/api/generated/erp/productChanges/api/productChangeConnectionsApi.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ConnectRequestsToProductChangeDialogProps {
	productChangeId: number;
	onCompleted: () => Promise<void>;
}

export const ConnectRequestsToProductChangeDialog = ({
	productChangeId,
	onCompleted,
}: ConnectRequestsToProductChangeDialogProps) => {
	return (
		<ServerSideDataGridModel
			gridId={"D229B2392C9E449A"}
			initialParams={{ searchQuery: "" }}
			fetchData={({ searchQuery, dataModelRequest }) =>
				ProductChangeRequestQueryApi.getProductChangeRequestsGridData({
					onlyNotConnectedToChange: true,
					searchQuery: searchQuery,
					dataModelRequest: dataModelRequest,
				})
			}
			getDataModelResult={(data) => data.requests}
			getRowId={(row) => row.productChangeRequestId}
			render={({ refreshData, dataGridProps, selectedRows }) => (
				<AavoDataGrid<ProductChangeRequestView>
					columns={[
						integerColumn({
							field: "productChangeRequestId",
							headerName: i18n.t("number_shortened"),
							width: 90,
						}),
						textColumn({
							field: "title",
							headerName: i18n.t("title"),
							width: 260,
						}),
						textColumn({
							field: "categoryName",
							headerName: i18n.t("category"),
							width: 160,
						}),
						enumColumn({
							field: "state",
							headerName: i18n.t("state"),
							enumLabels: getProductChangeRequestStateLabels(),
							width: 200,
						}),
					]}
					actionBarComponents={
						<HorizontalBox sx={{ gap: 2, alignItems: "center" }}>
							<AavoTextField
								label={i18n.t("search")}
								onSubmit={async (value) => {
									await refreshData({ searchQuery: value });
								}}
							/>
							<AsyncButton
								icon={faLink}
								label={i18n.t("connect")}
								variant={"outlined"}
								disabled={
									selectedRows.length === 0 ? i18n.t("select_at_least_one_row")
									: (
										selectedRows.some(
											(r) => r.productChangeId != null && r.productChangeId !== productChangeId,
										)
									) ?
										i18n.t("request_already_connected_to_change")
									:	undefined
								}
								onClick={async () => {
									await ProductChangeConnectionsApi.connectRequests({
										productChangeId,
										productChangeRequestIds: selectedRows.map((r) => r.productChangeRequestId),
									});
									await onCompleted();
								}}
							/>
						</HorizontalBox>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.productChanges.requestsToConnect.grid"),
						data: { productChangeId },
						rowFields: ["productChangeRequestId", "categoryId", "state", "productChangeId"],
						selectedRowPathSegment: (row) => `${i18n.t("request")} ${row.productChangeRequestId}`,
						selectedRowsPathSegment: (count) => i18n.t("product_change_requests_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
